'use strict';

/**
 * Per-position customer site (WERKS) selector for basket / RFQ positions (EPROC-10908).
 *
 * Renders an AngularJS Material autocomplete (md-autocomplete) over the sites that are selectable
 * for the document's company code (company-code-id). The list is loaded once from customerSiteService
 * (GET /business-partner/customer/sites/selectable) and filtered client-side. The selected site.code
 * is mirrored into a hidden input (input-name) so it is submitted with the surrounding VSL form and
 * picked up by the engine (tmpvar site_code).
 *
 * On a company-code change the list is reloaded and a no-longer-valid selection is reset, with a hint
 * to the user (AC 12: Position.Site.CompanyCode = Document.CompanyCode).
 */
module.exports = (app) => app.directive('customerSiteSelection', /* @ngInject */ function () {
	return {
		restrict: 'E',
		scope: {
			value: '=?',
			initValue: '=?',
			inputId: '@?',
			inputName: '@?',
			companyCodeId: '@?',
			customerCompanyId: '@?',
			showLabel: '=?',
			readonly: '=?',
			labelClass: '@?',
			inputClass: '@?',
		},
		templateUrl: `./${BUILD_PATH}/customer-site-selection.directive.html`,
		controller: /* @ngInject */ function ($scope, customerSiteService) {
			const DEFAULT_LABEL_CLASS = 'col-auto';
			const DEFAULT_INPUT_CLASS = 'col';

			$scope.loading = false;
			$scope.invalidated = false;
			$scope.sites = [];
			$scope.searchText = '';
			$scope.selectedSite = null;
			$scope.selectedCode = '';
			$scope.resolvedInputId = null;
			$scope.resolvedInputName = null;

			$scope.filterSites = filterSites;
			$scope.onSelectedItemChange = onSelectedItemChange;
			$scope.clearSelection = clearSelection;

			init();

			function init() {
				if (typeof $scope.showLabel === 'undefined') {
					$scope.showLabel = true;
				}
				if (typeof $scope.readonly === 'undefined') {
					$scope.readonly = false;
				}
				if (!$scope.labelClass) {
					$scope.labelClass = DEFAULT_LABEL_CLASS;
				}
				if (!$scope.inputClass) {
					$scope.inputClass = DEFAULT_INPUT_CLASS;
				}

				$scope.resolvedInputName = $scope.inputName || $scope.inputId || 'site_code';
				$scope.resolvedInputId = $scope.inputId || $scope.resolvedInputName;

				$scope.selectedCode = resolveCurrentValue();

				loadSites(false);

				$scope.$watch('companyCodeId', (newValue, oldValue) => {
					if (newValue === oldValue) {
						return;
					}
					// Nur ein echter Wechsel (vorher war bereits ein Buchungskreis gesetzt) invalidiert
					// die Auswahl + informiert den Nutzer (AC 12). Die initiale Befuellung der Isolate-
					// Scope-Bindung beim ersten $digest ist KEIN Wechsel.
					const isRealChange = typeof oldValue !== 'undefined' && oldValue !== null && oldValue !== '';
					loadSites(isRealChange);
				});

				// WICHTIG: value-Watch VOR initValue-Watch registrieren. Beide feuern beim ersten
				// $digest; der zuletzt registrierte gewinnt. value ist ohne explizites value-Attribut
				// undefined und wuerde sonst die aus initValue gesetzte Vorbelegung wieder leeren.
				$scope.$watch('value', (newValue, oldValue) => {
					if (newValue === oldValue && typeof oldValue !== 'undefined') {
						return;
					}
					// Leerer value beim Initial-Digest darf eine vorhandene initValue-Vorbelegung
					// nicht ueberschreiben.
					if (!newValue && $scope.initValue) {
						return;
					}
					if ((newValue || '') !== $scope.selectedCode) {
						$scope.selectedCode = newValue || '';
						syncSelectedSite();
					}
				});

				// initValue (gespeicherter site_code) wird bei =-Bindings erst beim ersten $digest
				// befuellt -> per Watch uebernehmen, sonst bleibt die Vorbelegung leer.
				$scope.$watch('initValue', (newValue, oldValue) => {
					if (newValue === oldValue) {
						return;
					}
					if ($scope.value) {
						return;
					}
					if ((newValue || '') !== $scope.selectedCode) {
						$scope.selectedCode = newValue || '';
						syncSelectedSite();
					}
				});
			}

			function loadSites(invalidateOnChange) {
				const companyCodeId = normalizeValue($scope.companyCodeId);
				if (!companyCodeId) {
					$scope.sites = [];
					if (invalidateOnChange) {
						resetSelection();
					}
					return;
				}

				$scope.loading = true;
				customerSiteService
					.getSelectable(companyCodeId)
					.then((sites) => {
						$scope.sites = sites || [];
						// Vorausgewaehlter Standort gehoert nicht (mehr) zum Buchungskreis -> verwerfen.
						if ($scope.selectedCode && !$scope.sites.some((site) => site.code === $scope.selectedCode)) {
							if (invalidateOnChange) {
								$scope.invalidated = true;
							}
							resetSelection();
						} else {
							syncSelectedSite();
						}
					})
					.catch((error) => {
						console.error(error);
						$scope.sites = [];
					})
					.finally(() => {
						$scope.loading = false;
						applySafe();
					});
			}

			function filterSites(query) {
				const normalizedQuery = normalizeValue(query).toLowerCase();
				if (!normalizedQuery) {
					return $scope.sites;
				}

				return $scope.sites.filter((site) =>
					(site.label || '').toLowerCase().indexOf(normalizedQuery) !== -1);
			}

			function onSelectedItemChange(site) {
				$scope.invalidated = false;
				$scope.selectedSite = site || null;
				$scope.selectedCode = site ? (site.code || '') : '';
				setExternalValue($scope.selectedCode);
			}

			function syncSelectedSite() {
				$scope.selectedSite = $scope.sites.find((site) => site.code === $scope.selectedCode) || null;
			}

			function clearSelection() {
				if ($scope.readonly) {
					return;
				}
				$scope.invalidated = false;
				$scope.searchText = '';
				resetSelection();
			}

			function resetSelection() {
				$scope.selectedSite = null;
				$scope.selectedCode = '';
				setExternalValue('');
			}

			function setExternalValue(value) {
				try {
					$scope.value = value;
				} catch (error) {
					// Non-assignable expression: rely on the hidden input value.
				}
			}

			function resolveCurrentValue() {
				return $scope.value || $scope.initValue || '';
			}

			function normalizeValue(value) {
				if (typeof value === 'undefined' || value === null) {
					return '';
				}

				return String(value).trim();
			}

			function applySafe() {
				if (!$scope.$$phase) {
					$scope.$apply();
				}
			}
		},
	};
});
